/**
 * useLiveState — single WebSocket to the panel backend. The server sends a
 * full-state "hello" on connect, then incremental messages; this hook folds
 * them into one UI state and reconnects with backoff when the link drops.
 */
import { useEffect, useReducer } from "react";
import type { BridgeSnapshot, FullState, LogLine, MarketResolution, MarketSearchState, MatchSignal, MatchStateEvent, PrecheckRow, UiMessage, UnitStatus, WalletView, WatchGroup } from "../types.ts";
import type { CategoryNeeds } from "../market-needs.ts";
import { mergeLiveEvent, type FoldedEvent } from "../fold-events.ts";

const KEEP = 200;

export type UiState = {
  wsConnected: boolean;
  mode: "paper" | "live";
  units: UnitStatus[];
  snapshot: BridgeSnapshot | null;
  matchScores: Record<string, MatchStateEvent>;
  tap: { connected: boolean; detail: string };
  counts: { signals: number; orders: number; fills: number; rejected: number };
  signals: MatchSignal[];
  events: FoldedEvent[];
  logs: LogLine[];
  resolutions: MarketResolution[];
  resolveErrors: Record<string, string>;
  precheck: { ts: string; rows: PrecheckRow[] } | null;
  /** match id → where its Polymarket search stands. Only groups that are not
   *  plainly bound appear; see MarketSearchState. */
  marketSearch: Record<string, MarketSearchState>;
  watchGroups: WatchGroup[];
  wallets: WalletView[];
  /** categoria → famiglie di mercato che le servono, dalle regole del bot.
   *  Vuoto su un backend vecchio: il badge resta verde invece di inventare. */
  categoryNeeds: CategoryNeeds;
};

const initial: UiState = {
  wsConnected: false,
  mode: "paper",
  units: [],
  snapshot: null,
  matchScores: {},
  tap: { connected: false, detail: "…" },
  counts: { signals: 0, orders: 0, fills: 0, rejected: 0 },
  signals: [],
  events: [],
  logs: [],
  resolutions: [],
  resolveErrors: {},
  precheck: null,
  marketSearch: {},
  watchGroups: [],
  wallets: [],
  categoryNeeds: {},
};

const byMatch = (xs: MarketSearchState[]): Record<string, MarketSearchState> =>
  Object.fromEntries(xs.map((x) => [x.match, x]));

/** Unresolved first, then most-recently-updated (mirrors the server's order). */
const sortResolutions = (xs: MarketResolution[]): MarketResolution[] =>
  [...xs].sort((a, b) => (a.status === "unresolved" ? 0 : 1) - (b.status === "unresolved" ? 0 : 1) || b.updatedAt.localeCompare(a.updatedAt));

type Action = { t: "msg"; m: UiMessage } | { t: "ws"; connected: boolean };

const clip = <T,>(xs: T[]) => (xs.length > KEEP ? xs.slice(xs.length - KEEP) : xs);

function reducer(s: UiState, a: Action): UiState {
  if (a.t === "ws") return { ...s, wsConnected: a.connected };
  const m = a.m;
  switch (m.type) {
    case "hello": {
      const st: FullState = m.state;
      return {
        ...s,
        wsConnected: true,
        mode: st.mode,
        units: st.units,
        snapshot: st.snapshot,
        matchScores: st.matchScores ?? {},
        tap: st.signalsTap,
        counts: st.counts,
        signals: st.recentSignals,
        events: st.recentEvents,
        logs: st.recentLogs,
        resolutions: sortResolutions(st.marketResolutions),
        marketSearch: byMatch(st.marketSearch ?? []),
        watchGroups: st.watchGroups ?? [],
        wallets: st.wallets ?? [],
        categoryNeeds: (st.categoryNeeds ?? {}) as CategoryNeeds,
      };
    }
    case "signal": {
      // the consolidator can legitimately re-deliver a signal (same id) after a
      // restart-and-resync — replace the existing row instead of duplicating it
      const known = s.signals.findIndex((x) => x.id === m.signal.id);
      if (known >= 0) {
        const signals = [...s.signals];
        signals[known] = m.signal;
        return { ...s, signals };
      }
      return { ...s, counts: { ...s.counts, signals: s.counts.signals + 1 }, signals: clip([...s.signals, m.signal]) };
    }
    case "bot-event": {
      const counts = { ...s.counts };
      if (m.kind === "order") counts.orders++;
      else if (m.kind === "fill") counts.fills++;
      else if (m.kind === "rejected") counts.rejected++;
      return { ...s, counts, events: clip(mergeLiveEvent(s.events, { ts: m.ts, kind: m.kind, text: m.text })) };
    }
    case "snapshot":
      return { ...s, snapshot: m.snapshot };
    case "match-state":
      return { ...s, matchScores: { ...s.matchScores, [m.state.match]: m.state } };
    case "proc-status":
      return { ...s, units: m.units };
    case "signals-tap":
      return { ...s, tap: { connected: m.connected, detail: m.detail } };
    case "mode-changed":
      return { ...s, mode: m.mode };
    case "log-line":
      return { ...s, logs: clip([...s.logs, m.line]) };
    case "market-resolution": {
      const resolutions = sortResolutions([...s.resolutions.filter((r) => r.match !== m.resolution.match), m.resolution]);
      const resolveErrors = { ...s.resolveErrors };
      if (m.resolution.status === "resolved") delete resolveErrors[m.resolution.match]; // success clears the stale error
      return { ...s, resolutions, resolveErrors };
    }
    case "market-resolution-removed": {
      const resolveErrors = { ...s.resolveErrors };
      delete resolveErrors[m.match];
      return { ...s, resolutions: s.resolutions.filter((r) => r.match !== m.match), resolveErrors };
    }
    case "resolve-error":
      return { ...s, resolveErrors: { ...s.resolveErrors, [m.match]: m.error } };
    case "precheck":
      return { ...s, precheck: { ts: m.ts, rows: m.rows } };
    // the whole map, not a delta: it is at most one entry per watch-list match
    case "market-search":
      return { ...s, marketSearch: byMatch(m.states) };
    case "watchlist":
      return { ...s, watchGroups: m.groups };
    case "wallets":
      return { ...s, wallets: m.wallets };
    default:
      return s;
  }
}

export function useLiveState(): UiState {
  const [state, dispatch] = useReducer(reducer, initial);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let closed = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      ws = new WebSocket(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`);
      ws.onopen = () => {
        attempts = 0;
        dispatch({ t: "ws", connected: true });
      };
      ws.onmessage = (e) => {
        if (e.data === "pong") return;
        try {
          dispatch({ t: "msg", m: JSON.parse(e.data) });
        } catch {
          /* ignore malformed frame */
        }
      };
      ws.onclose = () => {
        dispatch({ t: "ws", connected: false });
        if (closed) return;
        timer = setTimeout(connect, Math.min(15_000, 1000 * 2 ** attempts++));
      };
      ws.onerror = () => ws?.close();
    };
    connect();

    return () => {
      closed = true;
      if (timer) clearTimeout(timer);
      ws?.close();
    };
  }, []);

  return state;
}
